<template>
  <div
    class="ai-widget"
    @keydown.esc="onEscape"
  >
    <button
      v-if="!open"
      type="button"
      class="ai-launcher"
      :title="launcherTitle"
      :aria-label="launcherTitle"
      :style="launcherBottom ? { bottom: `${launcherBottom}px` } : null"
      @click="toggle(true)"
    >
      <ai-icon
        name="assistant"
        :size="22"
      />
      <span
        v-if="runningCount"
        class="ai-launcher__badge"
      >
        {{ runningCount }}
      </span>
      <!-- New side work findings (administrators): the corner opposite the running count. -->
      <span
        v-if="newFindings"
        class="ai-launcher__badge is-findings"
      >
        {{ newFindings > 99 ? '99+' : newFindings }}
      </span>
    </button>

    <!-- Files dropped anywhere on the window are attached to the message being written. -->
    <section
      v-else
      class="ai-window"
      :class="{ 'is-expanded': expanded, 'has-panel': !!panel }"
      role="dialog"
      aria-label="AI Ассистент"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <header class="ai-header">
        <button
          type="button"
          class="ai-icon-button ai-header__history"
          :class="{ 'is-active': sidebar }"
          title="История"
          @click="sidebar = !sidebar"
        >
          <ai-icon name="history" />
        </button>
        <div class="ai-header__title">
          <ai-icon
            name="assistant"
            :size="18"
          />
          AI Ассистент
        </div>
        <div
          class="ai-segmented"
          role="tablist"
        >
          <button
            v-for="item in modes"
            :key="item.key"
            type="button"
            role="tab"
            :aria-selected="mode === item.key ? 'true' : 'false'"
            :class="{ 'is-active': mode === item.key && !panelView }"
            @click="switchMode(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
        <context-meter
          class="ai-header__context"
          :context="context"
        />
        <button
          v-if="!diagnostic && sidework.canManage"
          type="button"
          class="ai-icon-button ai-header__badged"
          :class="{ 'is-active': view === 'sidework' }"
          :title="newFindings ? `Сторонние проекты · новых находок: ${newFindings}` : 'Сторонние проекты'"
          :aria-label="newFindings ? `Сторонние проекты, новых находок: ${newFindings}` : 'Сторонние проекты'"
          @click="view = view === 'sidework' ? 'thread' : 'sidework'"
        >
          <ai-icon name="project" />
          <span
            v-if="newFindings"
            class="ai-header__badge"
          >{{ newFindings > 99 ? '99+' : newFindings }}</span>
        </button>
        <button
          v-if="!diagnostic"
          type="button"
          class="ai-icon-button ai-header__badged"
          :class="{ 'is-active': view === 'memory' }"
          :title="memoryPending ? `Память · предложений: ${memoryPending}` : 'Память'"
          :aria-label="memoryPending ? `Память, предложений: ${memoryPending}` : 'Память'"
          @click="view = view === 'memory' ? 'thread' : 'memory'"
        >
          <ai-icon name="memory" />
          <span
            v-if="memoryPending"
            class="ai-header__badge"
          >{{ memoryPending > 99 ? '99+' : memoryPending }}</span>
        </button>
        <button
          v-if="canConfigure"
          type="button"
          class="ai-icon-button"
          :class="{ 'is-active': view === 'settings' }"
          title="Настройки ассистента"
          @click="view = view === 'settings' ? 'thread' : 'settings'"
        >
          <ai-icon name="settings" />
        </button>
        <button
          type="button"
          class="ai-icon-button ai-header__expand"
          :title="expanded ? 'Уменьшить окно' : 'Развернуть окно'"
          @click="expanded = !expanded"
        >
          <ai-icon :name="expanded ? 'collapse' : 'expand'" />
        </button>
        <div class="ai-header__end">
          <button
            type="button"
            class="ai-icon-button"
            title="Закрыть ассистента"
            aria-label="Закрыть ассистента"
            @click="toggle(false)"
          >
            <ai-icon name="close" />
          </button>
        </div>
      </header>

      <div
        v-if="banner"
        class="ai-banner ai-tone"
        :class="`is-${banner.tone}`"
      >
        <ai-icon
          :name="banner.tone === 'danger' ? 'alert' : 'info'"
          :size="15"
        />
        <span>{{ banner.text }}</span>
        <button
          v-if="banner.settings && canConfigure && !(view === 'settings' && settingsTab === banner.settings)"
          type="button"
          class="ai-link-button"
          @click="openSettings(banner.settings)"
        >
          Открыть настройки
        </button>
      </div>

      <!-- The service cannot be used at all: what is wrong and how to fix it, instead of the chat. -->
      <div
        v-if="diagnostic"
        class="ai-body"
      >
        <div
          class="ai-diagnostic"
          role="alert"
        >
          <div class="ai-diagnostic__badge ai-tone is-warning">
            <ai-icon
              :name="diagnostic.icon"
              :size="24"
            />
          </div>
          <h3 class="ai-diagnostic__title">
            {{ diagnostic.title }}
          </h3>
          <p class="ai-diagnostic__text">
            {{ diagnostic.text }}
          </p>
          <button
            type="button"
            class="ai-button is-primary"
            :disabled="retrying"
            @click="retry"
          >
            <span
              v-if="retrying"
              class="ai-spinner ai-spinner--small"
            />
            <ai-icon
              v-else
              name="resume"
              :size="14"
            />
            Повторить
          </button>
        </div>
      </div>

      <div
        v-else
        class="ai-body"
      >
        <div
          v-if="sidebar"
          class="ai-sidebar-backdrop"
          aria-hidden="true"
          @click="sidebar = false"
        />
        <nav
          v-if="sidebar"
          class="ai-sidebar"
          aria-label="История"
        >
          <button
            type="button"
            class="ai-button is-primary ai-sidebar__new"
            :disabled="blocked"
            @click="startNew"
          >
            <ai-icon name="plus" />
            {{ mode === 'investigation' ? 'Новое расследование' : 'Новый чат' }}
          </button>
          <ul class="ai-sidebar__list">
            <li
              v-for="item in list"
              :key="item.id"
              class="ai-conv"
              :class="{ 'is-active': item.id === activeId && view === 'thread', 'is-confirming': deleting === item.id }"
            >
              <input
                v-if="editing === item.id"
                ref="rename"
                v-model="editTitle"
                class="ai-input ai-conv__edit"
                maxlength="120"
                @keydown.enter.prevent="saveTitle(item)"
                @keydown.esc.stop="editing = null"
                @blur="saveTitle(item)"
              >
              <button
                v-else
                type="button"
                class="ai-conv__open"
                :title="item.title || 'Без названия'"
                @click="openFn(item.id)"
              >
                <span class="ai-conv__title">{{ item.title || 'Без названия' }}</span>
                <span class="ai-conv__meta">
                  <span
                    v-if="item.status === 'running'"
                    class="ai-spinner ai-spinner--small"
                  />
                  {{ listDate(item.updatedAt || item.createdAt) }}
                </span>
              </button>
              <confirm-inline
                v-if="deleting === item.id"
                class="ai-conv__confirm"
                text="Удалить диалог?"
                @confirm="remove(item)"
                @cancel="deleting = null"
              />
              <span
                v-else-if="editing !== item.id"
                class="ai-conv__actions"
              >
                <button
                  type="button"
                  class="ai-icon-button is-small"
                  title="Переименовать"
                  @click="startRename(item)"
                >
                  <ai-icon
                    name="edit"
                    :size="14"
                  />
                </button>
                <button
                  type="button"
                  class="ai-icon-button is-small"
                  title="Удалить"
                  @click="deleting = item.id"
                >
                  <ai-icon
                    name="trash"
                    :size="14"
                  />
                </button>
              </span>
            </li>
          </ul>
          <p
            v-if="!list.length"
            class="ai-sidebar__empty"
          >
            {{ mode === 'investigation' ? 'Расследований пока нет' : 'Диалогов пока нет' }}
          </p>
          <confirm-inline
            v-else-if="clearing"
            class="ai-sidebar__clear"
            :text="mode === 'investigation' ? 'Удалить все расследования?' : 'Удалить все диалоги?'"
            action="Удалить все"
            @confirm="clearAll"
            @cancel="clearing = false"
          />
          <button
            v-else
            type="button"
            class="ai-link-button ai-sidebar__clear"
            @click="clearing = true"
          >
            Очистить историю
          </button>
        </nav>

        <main class="ai-main">
          <settings-panel
            v-if="view === 'settings'"
            :tab.sync="settingsTab"
            @close="view = 'thread'"
            @saved="onSettingsSaved"
          />
          <memory-panel
            v-else-if="view === 'memory'"
            @close="view = 'thread'"
          />
          <sidework-panel
            v-else-if="view === 'sidework' && sidework.canManage"
            @close="view = 'thread'"
          />
          <investigation-form
            v-else-if="view === 'form' && capabilities"
            :capabilities="capabilities"
            :sending="sending"
            :error="sendError"
            @cancel="view = 'thread'"
            @submit="startInvestigation"
          />
          <template v-else>
            <div
              v-if="mode === 'investigation' && !thread"
              class="ai-empty"
            >
              <ai-icon
                name="search"
                :size="28"
              />
              <p>
                Расследование — это самостоятельная проверка: ассистент изучает данные за период по выбранным
                компьютерам и готовит отчёт с доказательствами.
              </p>
              <button
                type="button"
                class="ai-button is-primary"
                :disabled="blocked || !investigationsAvailable"
                @click="view = 'form'"
              >
                <ai-icon name="plus" />
                Новое расследование
              </button>
            </div>
            <chat-thread
              v-else
              :thread="thread"
              :disabled="blocked"
              :busy="sending"
              @example="send"
              @resume="resume"
              @cite="openCite"
              @evidence="openEvidence"
            />
            <form
              v-if="composer"
              class="ai-composer"
              @submit.prevent="send()"
            >
              <ul
                v-if="attachments.length"
                class="ai-composer__files"
                aria-label="Приложенные файлы"
              >
                <li
                  v-for="chip in attachments"
                  :key="chip.key"
                >
                  <attachment-chip
                    :name="chip.name"
                    :size="chip.size"
                    :progress="chip.status === 'uploading' ? chip.progress : null"
                    :error="chip.error"
                    :removable="!sending"
                    @remove="removeAttachment(chip)"
                  />
                </li>
              </ul>
              <button
                v-if="mode === 'chat'"
                type="button"
                class="ai-icon-button ai-composer__attach"
                title="Приложить файлы"
                aria-label="Приложить файлы"
                :disabled="!attachable"
                @click="pickFiles"
              >
                <ai-icon
                  name="paperclip"
                  :size="18"
                />
              </button>
              <input
                ref="file"
                type="file"
                class="ai-composer__file"
                multiple
                tabindex="-1"
                aria-hidden="true"
                @change="onPick"
              >
              <textarea
                ref="input"
                v-model="draft"
                class="ai-composer__input"
                rows="1"
                maxlength="20000"
                :placeholder="placeholder"
                :disabled="blocked"
                @input="autoGrow"
                @paste="onPaste"
                @keydown.enter.exact.prevent="send()"
              />
              <button
                v-if="live"
                type="button"
                class="ai-composer__button is-stop"
                title="Остановить"
                @click="cancel"
              >
                <ai-icon name="stop" />
              </button>
              <button
                v-else
                type="submit"
                class="ai-composer__button"
                :title="sendTitle"
                :disabled="!canSend"
              >
                <ai-icon name="send" />
              </button>
            </form>
            <p
              v-if="sendError && view === 'thread'"
              class="ai-composer__error"
            >
              {{ sendError }}
            </p>
          </template>
          <div
            v-if="dragging && attachable"
            class="ai-drop"
            aria-hidden="true"
          >
            <ai-icon
              name="paperclip"
              :size="26"
            />
            <span class="ai-drop__title">Отпустите, чтобы приложить файлы</span>
            <span class="ai-drop__hint">До {{ maxFiles }} файлов в сообщении, каждый не больше 50 МБ</span>
          </div>
        </main>

        <evidence-panel
          v-if="panel && thread && view === 'thread'"
          ref="evidence"
          :key="panel.messageId"
          :conversation-id="thread.conversation.id"
          :ids="panel.ids"
          :focus="panel.focus"
          :initial-tab="panel.tab"
          @close="panel = null"
        />
      </div>

      <memory-conflict
        v-if="conflict"
        :key="conflict.item.id"
        :item="conflict.item"
        @done="finishConflict"
      />
    </section>
  </div>
</template>

<script>
  import { deleteUpload, errorText, isCancelled, uploadCanceller, uploadFile } from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import AttachmentChip from './AttachmentChip.vue';
  import ChatThread from './ChatThread.vue';
  import ConfirmInline from './ConfirmInline.vue';
  import ContextMeter from './ContextMeter.vue';
  import EvidencePanel from './EvidencePanel.vue';
  import InvestigationForm from './InvestigationForm.vue';
  import MemoryConflict from './MemoryConflict.vue';
  import MemoryPanel from './MemoryPanel.vue';
  import SettingsPanel from './SettingsPanel.vue';
  import SideworkPanel from './SideworkPanel.vue';
  import { listDate } from './format';

  const MODES = [
    { key: 'chat', label: 'Чат' },
    { key: 'investigation', label: 'Расследования' },
  ];
  const STATUS_REFRESH_MS = 60000;
  // Diagnostic screen by the problem code of the web panel proxy. Users see no configuration details:
  // the proxy writes them to the web backend log for the server administrator.
  const DIAGNOSTICS = {
    'not-configured': {
      icon: 'plug',
      title: 'Ассистент не подключён',
      text: 'Ассистент ещё не подключён к веб-панели. Обратитесь к администратору сервера.',
    },
    unreachable: {
      icon: 'alert',
      title: 'Ассистент не отвечает',
      text: 'Служба ассистента сейчас недоступна. Повторите позже или обратитесь к администратору сервера.',
    },
    unauthorized: {
      icon: 'key',
      title: 'Ассистент отклонил подключение',
      text: 'Веб-панель не смогла подключиться к ассистенту. Обратитесь к администратору сервера.',
    },
  };
  // Launcher geometry (see .ai-launcher): offset from the viewport corner, size, gap above app toasts.
  const LAUNCHER_OFFSET = 24;
  const LAUNCHER_SIZE = 52;
  const LAUNCHER_GAP = 10;
  // Files of one message (the service's limits).
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  const TOO_MANY_FILES = `Не больше ${MAX_FILES} файлов в сообщении`;
  let chipKey = 0;

  // A drag that carries files (not text or a link from the page).
  const hasFiles = (event) =>
    !!event.dataTransfer && Array.prototype.indexOf.call(event.dataTransfer.types || [], 'Files') !== -1;

  export default {
    name: 'ChatWidget',
    components: {
      AiIcon,
      AttachmentChip,
      ChatThread,
      ConfirmInline,
      ContextMeter,
      EvidencePanel,
      InvestigationForm,
      MemoryConflict,
      MemoryPanel,
      SettingsPanel,
      SideworkPanel,
    },
    // Memory conflicts of the panel and of the suggestion cards are decided in one dialog over the window.
    provide() {
      return { aiMemory: { resolve: this.openConflict } };
    },
    data: () => ({
      modes: MODES,
      view: 'thread',
      sidebar: window.innerWidth > 900,
      expanded: false,
      draft: '',
      sending: false,
      sendError: '',
      panel: null,
      editing: null,
      editTitle: '',
      // The conversation whose deletion is being confirmed; «Очистить историю» is being confirmed.
      deleting: null,
      clearing: false,
      timer: null,
      launcherBottom: 0,
      settingsTab: 'model',
      retrying: false,
      // { item, done }: the memory entry whose conflict is being decided.
      conflict: null,
      // Files of the message being written: { key, name, size, status (uploading | done | error), progress,
      // error, upload (the service's upload), cancel }.
      attachments: [],
      maxFiles: MAX_FILES,
      // Nesting depth of dragenter/dragleave over the window while files are dragged.
      dragDepth: 0,
    }),
    computed: {
      state() {
        return this.$store.state.assistant;
      },
      open() {
        return this.state.open;
      },
      mode() {
        return this.state.mode;
      },
      capabilities() {
        return this.state.capabilities;
      },
      thread() {
        return this.$store.getters['assistant/thread'];
      },
      activeId() {
        return this.$store.getters['assistant/activeId'];
      },
      list() {
        return this.$store.getters['assistant/list'];
      },
      canConfigure() {
        return this.$store.getters['assistant/canConfigure'];
      },
      runningCount() {
        return this.$store.getters['assistant/runningCount'];
      },
      // Views that replace the conversation (the mode tabs are not highlighted there).
      panelView() {
        return ['settings', 'memory', 'sidework'].includes(this.view);
      },
      // Side work control: administrators only ({available, canManage, newFindings}).
      sidework() {
        return this.$store.getters['assistant/sidework'];
      },
      newFindings() {
        return this.sidework.canManage ? this.sidework.newFindings || 0 : 0;
      },
      launcherTitle() {
        return this.newFindings ? `AI Ассистент · новых находок: ${this.newFindings}` : 'AI Ассистент';
      },
      // Administrators see new findings on the launcher before the window is opened.
      isAdministrator() {
        return [1, 4].includes(this.$store.state.user.role);
      },
      memoryPending() {
        const counts = this.state.memoryCounts;
        return this.canConfigure && counts ? counts.pending || 0 : 0;
      },
      live() {
        return !!(this.thread && this.thread.live);
      },
      investigationsAvailable() {
        return !this.capabilities || this.capabilities.investigationsAvailable !== false;
      },
      context() {
        if (!this.thread) return null;
        return (this.thread.live && this.thread.live.context) || this.thread.context;
      },
      diagnostic() {
        const message = this.state.capabilitiesError;
        if (!message) return null;
        const known = DIAGNOSTICS[this.state.capabilitiesCode];
        return known || { icon: 'alert', title: 'Ассистент недоступен', text: message };
      },
      // The service works but something is missing: «Открыть настройки» leads to the tab that fixes it.
      banner() {
        const { status } = this.state;
        if (this.diagnostic || !status) return null;
        if (status.error) return { tone: 'danger', text: status.error };
        const model = status.model || {};
        const source = status.source || {};
        if (!model.configured) {
          const text = 'Модель не выбрана, и на сервере нет загруженной модели — ассистент пока не может отвечать.';
          return { tone: 'warning', text, settings: 'model' };
        }
        if (model.reachable === false) {
          return { tone: 'danger', text: 'Сервер модели недоступен. Проверьте, что он запущен.', settings: 'model' };
        }
        if (model.found === false) {
          return { tone: 'warning', text: `Модель «${model.chatModel}» не найдена на сервере.`, settings: 'model' };
        }
        if (source.ok === false) {
          return { tone: 'danger', text: source.error || 'Данные мониторинга сейчас недоступны.' };
        }
        if (source.error) return { tone: 'warning', text: source.error };
        if (status.queue && status.queue.waiting) {
          return { tone: 'info', text: `Модель занята, запросов в очереди: ${status.queue.waiting}.` };
        }
        return null;
      },
      blocked() {
        const model = (this.state.status && this.state.status.model) || {};
        return !!this.state.capabilitiesError || model.configured === false;
      },
      // The message field: a chat, and follow-up questions of an open conversation.
      composer() {
        return this.mode === 'chat' || !!this.thread;
      },
      attachable() {
        // Files go to chats only: an investigation works within its approved scope.
        return this.mode === 'chat' && this.view === 'thread' && this.composer && !this.diagnostic && !this.blocked
          && !this.sending;
      },
      dragging() {
        return this.dragDepth > 0;
      },
      // A message may be files only; every file must be uploaded first.
      canSend() {
        if (this.blocked || this.sending) return false;
        if (this.attachments.some((chip) => chip.status !== 'done')) return false;
        return !!this.draft.trim() || this.attachments.length > 0;
      },
      sendTitle() {
        if (this.attachments.some((chip) => chip.status === 'uploading')) return 'Дождитесь загрузки файлов';
        if (this.attachments.some((chip) => chip.status === 'error')) return 'Уберите файлы с ошибкой';
        return 'Отправить (Enter)';
      },
      placeholder() {
        if (this.live) return 'Ассистент отвечает…';
        return this.mode === 'investigation' ? 'Уточняющий вопрос по расследованию' : 'Спросите о данных мониторинга';
      },
    },
    watch: {
      open: {
        immediate: true,
        handler(value) {
          clearInterval(this.timer);
          if (!value) return;
          this.$store
            .dispatch('assistant/init')
            .then(() => {
              if (!this.state.capabilitiesError) this.$store.dispatch('assistant/loadMemoryCounts');
            })
            .catch(() => {});
          this.timer = setInterval(() => this.$store.dispatch('assistant/refreshStatus'), STATUS_REFRESH_MS);
          this.$nextTick(this.focusInput);
        },
      },
      activeId() {
        this.panel = null;
      },
      isAdministrator: {
        immediate: true,
        handler(value) {
          if (value) this.$store.dispatch('assistant/preload').catch(() => {});
        },
      },
      'sidework.canManage': function canManage(value) {
        if (!value && this.view === 'sidework') this.view = 'thread';
      },
    },
    mounted() {
      // App toasts (vue-toast-notification) stack in the bottom-right corner, right over the launcher:
      // watch their container and lift the launcher above them while they are shown.
      this.toastObserver = new MutationObserver(this.placeLauncher);
      this.toastObserver.observe(document.body, { childList: true });
      this.placeLauncher();
    },
    beforeDestroy() {
      clearInterval(this.timer);
      this.toastObserver.disconnect();
      this.$store.dispatch('assistant/closeStreams');
      // Files that were not sent: uploads stop, uploaded ones are removed.
      this.attachments.forEach((chip) => this.dropUpload(chip));
    },
    methods: {
      listDate,
      toggle(value) {
        this.$store.commit('assistant/SET', { open: value });
      },
      placeLauncher() {
        const stack = document.querySelector('.v-toast.v-toast--bottom');
        if (stack && stack !== this.toastStack) {
          this.toastStack = stack;
          // Toasts are shown and hidden with v-show (style) and enter/leave classes, then removed.
          this.toastObserver.observe(stack, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
          });
        }
        const right = stack ? stack.clientWidth - LAUNCHER_OFFSET - LAUNCHER_SIZE : 0;
        const tops = stack
          ? [...stack.querySelectorAll('.v-toast__item')]
              .filter((el) => el.offsetHeight && el.offsetLeft + el.offsetWidth > right)
              .map((el) => el.offsetTop)
          : [];
        this.launcherBottom = tops.length ? stack.clientHeight - Math.min(...tops) + LAUNCHER_GAP : 0;
      },
      onEscape() {
        if (this.conflict) this.finishConflict(null);
        else if (this.sidebar && window.innerWidth <= 900) this.sidebar = false;
        else if (this.panel) this.panel = null;
        else if (this.view !== 'thread') this.view = 'thread';
        else this.toggle(false);
      },
      async switchMode(mode) {
        this.view = 'thread';
        this.deleting = null;
        this.clearing = false;
        this.sendError = '';
        await this.$store.dispatch('assistant/setMode', mode).catch(() => {});
      },
      startNew() {
        this.sendError = '';
        this.$store.dispatch('assistant/newConversation');
        this.view = this.mode === 'investigation' ? 'form' : 'thread';
        if (window.innerWidth <= 900) this.sidebar = false;
        this.$nextTick(this.focusInput);
      },
      async openFn(id) {
        this.view = 'thread';
        this.sendError = '';
        if (window.innerWidth <= 900) this.sidebar = false;
        await this.$store.dispatch('assistant/openConversation', id);
      },
      // text: an example question (sent without the attached files); the composer otherwise.
      async send(text) {
        const example = typeof text === 'string';
        const value = (example ? text : this.draft).trim();
        const files = example ? [] : this.attachments;
        if (this.sending || this.live || this.blocked) return;
        if (files.some((chip) => chip.status !== 'done') || (!value && !files.length)) return;
        this.sending = true;
        this.sendError = '';
        try {
          const attachments = files.map((chip) => chip.upload.id);
          await this.$store.dispatch('assistant/send', { text: value, attachments });
          if (value === this.draft.trim()) this.draft = '';
          if (files.length) this.attachments = this.attachments.filter((chip) => !files.includes(chip));
          this.$nextTick(this.autoGrow);
        } catch (error) {
          this.sendError = errorText(error, 'Не удалось отправить сообщение');
        } finally {
          this.sending = false;
        }
      },
      async startInvestigation(body) {
        this.sending = true;
        this.sendError = '';
        try {
          await this.$store.dispatch('assistant/startInvestigation', body);
          this.view = 'thread';
        } catch (error) {
          this.sendError = errorText(error, 'Не удалось начать расследование');
        } finally {
          this.sending = false;
        }
      },
      async resume() {
        try {
          await this.$store.dispatch('assistant/resume', this.activeId);
        } catch (error) {
          this.sendError = errorText(error, 'Не удалось продолжить');
        }
      },
      cancel() {
        this.$store.dispatch('assistant/cancel', this.activeId).catch(() => {});
      },
      // related: evidence the answer may not cite (a file of the question) is shown among the related materials.
      openCite({ eid, messageId, ids, related }) {
        this.panel = { messageId, ids, focus: eid, tab: 'cited' };
        if (related && !ids.includes(eid)) {
          this.$nextTick(() => this.$refs.evidence && this.$refs.evidence.openRef(eid));
        }
      },
      openEvidence({ messageId, ids }) {
        this.panel = { messageId, ids, focus: null, tab: ids.length ? 'cited' : 'all' };
      },
      startRename(item) {
        this.editing = item.id;
        this.editTitle = item.title || '';
        this.$nextTick(() => {
          const input = this.$refs.rename && this.$refs.rename[0];
          if (input) input.select();
        });
      },
      async saveTitle(item) {
        if (this.editing !== item.id) return;
        const title = this.editTitle.trim();
        this.editing = null;
        if (title && title !== item.title) {
          await this.$store.dispatch('assistant/rename', { id: item.id, title }).catch(() => {});
        }
      },
      async remove(item) {
        this.deleting = null;
        await this.$store.dispatch('assistant/remove', item.id).catch(() => {});
      },
      async clearAll() {
        this.clearing = false;
        await this.$store.dispatch('assistant/clear', this.mode).catch(() => {});
      },
      openSettings(tab) {
        this.settingsTab = tab || 'model';
        this.view = 'settings';
      },
      onSettingsSaved() {
        this.$store.dispatch('assistant/refreshStatus');
        this.$store.dispatch('assistant/loadCapabilities');
      },
      async retry() {
        this.retrying = true;
        try {
          await this.$store.dispatch('assistant/reconnect');
        } finally {
          this.retrying = false;
        }
      },
      openConflict(item) {
        if (this.conflict) this.conflict.done(null);
        return new Promise((resolve) => {
          this.conflict = { item, done: resolve };
        });
      },
      finishConflict(result) {
        const { done } = this.conflict || {};
        this.conflict = null;
        this.$store.dispatch('assistant/loadMemoryCounts');
        if (done) done(result);
      },
      pickFiles() {
        if (this.attachable && this.$refs.file) this.$refs.file.click();
      },
      onPick(event) {
        this.addFiles(event.target.files);
        // The same file can be picked again after it was removed.
        event.target.value = '';
      },
      // Files pasted into the field (a screenshot from the clipboard); text is pasted as usual.
      onPaste(event) {
        const files = event.clipboardData && event.clipboardData.files;
        if (!files || !files.length || !this.attachable) return;
        event.preventDefault();
        this.addFiles(files);
      },
      onDragEnter(event) {
        if (!hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth += 1;
      },
      // Files are not opened by the browser when dropped where they cannot be attached.
      onDragOver(event) {
        if (!hasFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = this.attachable ? 'copy' : 'none';
      },
      onDragLeave(event) {
        if (!hasFiles(event)) return;
        this.dragDepth = Math.max(this.dragDepth - 1, 0);
      },
      onDrop(event) {
        if (!hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth = 0;
        if (this.attachable) this.addFiles(event.dataTransfer.files);
      },
      // Each file uploads at once; files over the limits are shown with the reason and not uploaded.
      addFiles(list) {
        const files = Array.from(list || []);
        if (!files.length || !this.attachable) return;
        this.sendError = '';
        const room = MAX_FILES - this.attachments.length;
        if (files.length > room) this.sendError = TOO_MANY_FILES;
        files.slice(0, Math.max(room, 0)).forEach((file) => {
          chipKey += 1;
          const chip = { key: chipKey, name: file.name, size: file.size, status: 'uploading', progress: 0 };
          Object.assign(chip, { error: '', upload: null, cancel: null });
          this.attachments.push(chip);
          if (file.size > MAX_FILE_SIZE) this.failUpload(chip, 'Файл больше 50 МБ');
          else if (!file.size) this.failUpload(chip, 'Файл пустой');
          else this.startUpload(chip, file);
        });
        this.$nextTick(this.focusInput);
      },
      failUpload(chip, error) {
        chip.status = 'error';
        chip.error = error;
      },
      async startUpload(chip, file) {
        const source = uploadCanceller();
        chip.cancel = source.cancel;
        try {
          const upload = await uploadFile(
            file,
            (value) => {
              chip.progress = value;
            },
            source.token,
          );
          chip.cancel = null;
          // Removed while the service was reading it.
          if (!this.attachments.includes(chip)) {
            deleteUpload(upload.id).catch(() => {});
            return;
          }
          Object.assign(chip, { upload, status: 'done', progress: 1 });
        } catch (error) {
          chip.cancel = null;
          if (!isCancelled(error)) this.failUpload(chip, errorText(error, 'Не удалось загрузить файл'));
        }
      },
      removeAttachment(chip) {
        this.attachments = this.attachments.filter((item) => item !== chip);
        this.dropUpload(chip);
        if (this.sendError === TOO_MANY_FILES) this.sendError = '';
        this.$nextTick(this.focusInput);
      },
      // Stops an upload in progress or removes an uploaded file that was not sent.
      dropUpload(chip) {
        if (chip.cancel) chip.cancel('Removed');
        if (chip.upload) deleteUpload(chip.upload.id).catch(() => {});
      },
      autoGrow() {
        const el = this.$refs.input;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
      },
      focusInput() {
        if (this.$refs.input && !this.blocked) this.$refs.input.focus();
      },
    },
  };
</script>

<style lang="scss" scoped>
  // Launcher and window sit above the app toasts (vue-toast-notification, z-index 1052)
  // and below the app modals (z-index 3000).
  .ai-launcher {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1060;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    padding: 0;
    border-radius: 50% !important;
    background: var(--ui-primary);
    color: #fff;
    box-shadow: var(--ui-shadow) !important;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease, bottom 0.2s ease;

    &:hover {
      background: var(--ui-primary-hover);
      transform: translateY(-1px);
    }
  }

  .ai-launcher__badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--ui-warning);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;

    &.is-findings {
      right: auto;
      left: -2px;
      background: var(--ui-danger);
    }
  }

  .ai-window {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1060;
    display: flex;
    flex-direction: column;
    width: 820px;
    max-width: calc(100vw - 32px);
    height: 720px;
    max-height: calc(100vh - 48px);
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-surface);
    box-shadow: var(--ui-shadow);
    color: var(--ui-text);
    font-size: 14px;
    overflow: hidden;

    &.has-panel {
      width: 1180px;
    }

    &.is-expanded {
      top: 16px;
      right: 16px;
      bottom: 16px;
      left: 16px;
      width: auto;
      max-width: none;
      height: auto;
      max-height: none;
    }
  }

  .ai-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-bottom: 1px solid var(--ui-border);
  }

  .ai-header__title {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-right: 6px;
    color: var(--ui-text);
    font-size: 15px;
    font-weight: 500;
    white-space: nowrap;

    .ai-icon {
      color: var(--ui-primary);
    }
  }

  .ai-header__context {
    flex: 1 1 auto;
    justify-content: flex-end;
  }

  // The close button stands apart at the right edge, after a divider, so it is easy to find.
  .ai-header__end {
    display: flex;
    flex: 0 0 auto;
    margin-left: auto;
    padding-left: 8px;
    border-left: 1px solid var(--ui-border);
  }

  // Header buttons with a counter (memory suggestions, new findings).
  .ai-header__badged {
    position: relative;
  }

  .ai-header__badge {
    position: absolute;
    top: 1px;
    right: 0;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    box-sizing: border-box;
    border-radius: 999px;
    background: var(--ui-warning);
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    text-align: center;
    pointer-events: none;
  }

  .ai-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 13px;

    &.is-info {
      background: var(--ui-surface-soft);
      color: var(--ui-text-muted);
    }
  }

  .ai-body {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  // A fixed column: long titles are ellipsised instead of widening it.
  .ai-sidebar {
    display: flex;
    flex: 0 0 240px;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
    width: 240px;
    min-width: 0;
    min-height: 0;
    padding: 10px 8px;
    overflow-x: hidden;
    border-right: 1px solid var(--ui-border);
    background: var(--ui-bg);
  }

  // Only shown on narrow screens, where the sidebar is a drawer over the chat.
  .ai-sidebar-backdrop {
    display: none;
  }

  .ai-sidebar__new {
    justify-content: center;
  }

  .ai-sidebar__list {
    flex: 1;
    min-height: 0;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
  }

  .ai-sidebar__empty {
    margin: 8px 6px;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-sidebar__clear {
    align-self: center;
    font-size: 12px;
  }

  .ai-confirm.ai-sidebar__clear {
    justify-content: center;
    padding: 4px 0;
  }

  // The whole row is the card: the open button spans it, rename/delete float over its right end
  // (shown on hover, keyboard focus and for the active conversation).
  .ai-conv {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    margin-bottom: 2px;
    border-radius: var(--ui-radius-sm);

    &:hover,
    &:focus-within,
    &.is-active {
      background: var(--ui-surface);
    }

    &.is-active {
      box-shadow: inset 2px 0 0 var(--ui-primary);
    }

    &:hover .ai-conv__actions,
    &:focus-within .ai-conv__actions,
    &.is-active .ai-conv__actions {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .ai-conv__open {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 1px;
    width: 100%;
    min-width: 0;
    padding: 7px 8px;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;

    // The list scrolls (clips overflow): keep the focus outline inside the card.
    &:focus-visible {
      outline-offset: -2px;
    }
  }

  .ai-conv:hover .ai-conv__open,
  .ai-conv:focus-within .ai-conv__open,
  .ai-conv.is-active .ai-conv__open {
    padding-right: 64px;
  }

  // Deletion is confirmed in a row under the title.
  .ai-conv.is-confirming {
    flex-wrap: wrap;

    .ai-conv__open {
      padding-right: 8px;
    }
  }

  .ai-conv__confirm {
    flex: 1 1 100%;
    justify-content: flex-start;
    padding: 0 8px 7px;
  }

  .ai-conv__title,
  .ai-conv__meta {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-conv__title {
    color: var(--ui-text);
    font-size: 13px;
  }

  .ai-conv__meta {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--ui-text-muted);
    font-size: 11px;
  }

  .ai-conv__actions {
    position: absolute;
    top: 50%;
    right: 4px;
    display: flex;
    gap: 2px;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-50%);
    transition: opacity 0.12s ease;
  }

  .ai-conv__edit {
    flex: 1 1 auto;
    min-width: 0;
    margin: 3px;
  }

  // Positioned for the drop highlight.
  .ai-main {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  // Files dragged over the window: the conversation column shows where they go. It lets the pointer
  // through, so the drag events stay on the elements below.
  .ai-drop {
    position: absolute;
    top: 8px;
    right: 8px;
    bottom: 8px;
    left: 8px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 2px dashed var(--ui-primary);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-surface);
    color: var(--ui-primary);
    text-align: center;
    pointer-events: none;
    isolation: isolate;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
      border-radius: inherit;
      background: var(--ui-primary-soft);
      opacity: 0.6;
    }
  }

  .ai-drop__title {
    color: var(--ui-text);
    font-size: 15px;
    font-weight: 500;
  }

  .ai-drop__hint {
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    max-width: 440px;
    margin: auto;
    padding: 24px;
    color: var(--ui-text-muted);
    text-align: center;

    .ai-icon {
      color: var(--ui-primary);
    }

    p {
      margin: 0;
      line-height: 1.5;
    }
  }

  .ai-diagnostic {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    max-width: 480px;
    margin: auto;
    padding: 24px;
    text-align: center;
  }

  .ai-diagnostic__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
  }

  .ai-diagnostic__title {
    margin: 4px 0 0;
    color: var(--ui-text);
    font-size: 17px;
    font-weight: 500;
  }

  .ai-diagnostic__text {
    margin: 0 0 6px;
    color: var(--ui-text-muted);
    font-size: 13px;
    line-height: 1.6;
  }

  // The files of the message take a row of their own above the field (flex-wrap).
  .ai-composer {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 8px;
    margin: 0 14px 12px;
    padding: 6px 6px 6px 4px;
    border: 1px solid var(--ui-border-strong);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-surface);

    &:focus-within {
      border-color: var(--ui-primary);
      box-shadow: var(--ui-focus);
    }
  }

  .ai-composer__files {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
    margin: 0;
    padding: 2px 0 0 4px;
    list-style: none;

    li {
      display: flex;
      min-width: 0;
      max-width: 100%;
    }
  }

  // Round, the size of the send button.
  .ai-composer__attach.ai-icon-button {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    border-radius: 50% !important;
  }

  .ai-composer__file {
    display: none;
  }

  // The composer frame above draws the border and the focus ring; the theme's own textarea
  // border, hover colour and focus ring would draw a second frame inside it.
  .ai-composer__input {
    flex: 1;
    min-height: 34px;
    max-height: 180px;
    padding: 7px 0;
    border: 0 !important;
    border-radius: 0;
    outline: none;
    background: transparent;
    box-shadow: none !important;
    color: var(--ui-text);
    font: inherit;
    line-height: 1.45;
    resize: none;

    &:hover,
    &:focus,
    &:focus-visible {
      border: 0 !important;
      outline: none !important;
      box-shadow: none !important;
    }
  }

  .ai-composer__button {
    display: inline-flex;
    flex: 0 0 34px;
    align-items: center;
    justify-content: center;
    height: 34px;
    padding: 0;
    border-radius: 50% !important;
    background: var(--ui-primary);
    color: #fff;
    cursor: pointer;

    &:disabled {
      background: var(--ui-bg-strong) !important;
      color: var(--ui-text-muted) !important;
      opacity: 1 !important;
      cursor: default;
    }

    &.is-stop {
      background: var(--ui-danger);
    }
  }

  .ai-composer__error {
    margin: -6px 16px 10px;
    color: var(--ui-danger);
    font-size: 13px;
  }

  @media (max-width: 900px) {
    .ai-window,
    .ai-window.has-panel {
      top: 8px;
      right: 8px;
      bottom: 8px;
      left: 8px;
      width: auto;
      max-width: none;
      height: auto;
      max-height: none;
    }

    // Drawers are positioned in the body, below the header and the banner.
    .ai-body {
      position: relative;
    }

    .ai-sidebar {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 3;
      flex-basis: auto;
      width: 260px;
      max-width: 85%;
      box-shadow: var(--ui-shadow);
    }

    .ai-sidebar-backdrop {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      display: block;
      background: var(--ui-text);
      opacity: 0.28;
      cursor: pointer;
    }

    .ai-header {
      gap: 4px;
      padding: 8px 6px;
    }

    .ai-header .ai-segmented button {
      padding: 4px 9px;
    }

    .ai-header__title,
    .ai-header__context {
      display: none;
    }

    // The window fills the screen here: nothing to expand (over the shared .ai-icon-button display).
    .ai-header .ai-header__expand {
      display: none;
    }

    ::v-deep .ai-panel {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      z-index: 4;
      width: 380px;
      max-width: 100%;
    }
  }
</style>

<style lang="scss">
  // Shared controls of the assistant (scoped to the widget root).
  .ai-widget {
    font-family: inherit;

    .ai-icon {
      flex: 0 0 auto;
    }

    // The redesign theme (assets/styles/redesign/redesign.css) forces on every <button> a 1px border,
    // a 32px min-height, its own radius and shadow (all !important) and a grey disabled look.
    // The assistant draws its controls itself: drop the theme border here, every control below
    // restores what it needs (with !important where the theme uses it).
    button {
      min-height: 0;
      border: 0 !important;
      font: inherit;

      &:disabled {
        border-color: transparent !important;
        background: transparent !important;
        color: var(--ui-text-muted) !important;
        opacity: 0.55 !important;
        cursor: default;
      }
    }

    // Bordered button; also used on router links. Variants: is-primary, is-small, is-ghost.
    .ai-button {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      box-sizing: border-box;
      min-height: 32px;
      padding: 5px 14px;
      border: 1px solid var(--ui-border-strong) !important;
      border-radius: var(--ui-radius-sm) !important;
      background: var(--ui-surface);
      color: var(--ui-text);
      font: inherit;
      font-size: 13px;
      line-height: 1.3;
      text-decoration: none;
      cursor: pointer;

      &:hover:not(:disabled) {
        border-color: var(--ui-primary) !important;
        color: var(--ui-primary);
        text-decoration: none;
      }

      &.is-primary {
        border-color: var(--ui-primary) !important;
        background: var(--ui-primary);
        color: #fff;

        &:hover:not(:disabled) {
          border-color: var(--ui-primary-hover) !important;
          background: var(--ui-primary-hover);
          color: #fff;
        }
      }

      // Confirms a deletion (ConfirmInline).
      &.is-danger {
        border-color: var(--ui-danger) !important;
        background: var(--ui-danger);
        color: #fff;

        &:hover:not(:disabled) {
          border-color: var(--ui-danger) !important;
          color: #fff;
          filter: brightness(0.92);
        }
      }

      &.is-small {
        min-height: 26px;
        padding: 2px 10px;
      }

      // Secondary action next to content: answer footer, evidence links, table toggles.
      &.is-ghost {
        border-color: var(--ui-border) !important;
        background: transparent;
        color: var(--ui-primary);

        &:hover:not(:disabled) {
          border-color: var(--ui-primary) !important;
          background: var(--ui-primary-soft);
          color: var(--ui-primary-hover);
        }
      }

      &:disabled {
        border-color: var(--ui-border) !important;
        background: var(--ui-bg-strong) !important;
        color: var(--ui-text-muted) !important;
        opacity: 1 !important;
      }
    }

    .ai-icon-button {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border-radius: var(--ui-radius-sm);
      background: transparent;
      color: var(--ui-text-muted);
      cursor: pointer;

      &:hover,
      &.is-active {
        background: var(--ui-bg-strong);
        color: var(--ui-text);
      }

      &.is-small {
        width: 26px;
        height: 26px;
      }
    }

    // Borderless inline text action.
    .ai-link-button {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 0;
      background: none;
      color: var(--ui-primary);
      font: inherit;
      font-size: 13px;
      cursor: pointer;

      &:hover:not(:disabled) {
        color: var(--ui-primary-hover);
        text-decoration: underline;
      }

      &:disabled {
        opacity: 1 !important;
      }
    }

    .ai-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .ai-chip-button {
      padding: 5px 12px;
      border: 1px solid var(--ui-border) !important;
      border-radius: 999px !important;
      background: var(--ui-surface);
      color: var(--ui-text);
      font: inherit;
      font-size: 13px;
      text-align: left;
      cursor: pointer;

      &:hover:not(:disabled) {
        border-color: var(--ui-primary) !important;
      }

      &.is-active {
        border-color: var(--ui-primary) !important;
        background: var(--ui-primary-soft);
        color: var(--ui-primary);
      }

      &:disabled {
        border-color: var(--ui-border) !important;
        background: var(--ui-surface) !important;
      }
    }

    .ai-segmented {
      display: inline-flex;
      flex: 0 0 auto;
      padding: 2px;
      border-radius: var(--ui-radius-sm);
      background: var(--ui-bg-strong);

      button {
        padding: 4px 12px;
        border-radius: calc(var(--ui-radius-sm) - 2px) !important;
        background: transparent;
        color: var(--ui-text-muted);
        font: inherit;
        font-size: 13px;
        cursor: pointer;

        &:hover:not(:disabled) {
          color: var(--ui-text);
        }

        &.is-active {
          background: var(--ui-surface);
          color: var(--ui-text);
          box-shadow: var(--ui-shadow-sm) !important;
        }
      }
    }

    // Status surface tinted with a tone token: a translucent layer under the content
    // (LibSass has no colour functions for CSS variables). Text stays dark, the icon takes the tone.
    .ai-tone {
      position: relative;
      isolation: isolate;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: -1;
        border-radius: inherit;
        opacity: 0.12;
        pointer-events: none;
      }

      &.is-danger,
      &.is-warning,
      &.is-success {
        color: var(--ui-text);
      }

      &.is-danger::before {
        background: var(--ui-danger);
      }

      &.is-warning::before {
        background: var(--ui-warning);
      }

      &.is-success::before {
        background: var(--ui-success);
      }

      &.is-danger > .ai-icon {
        color: var(--ui-danger);
      }

      &.is-warning > .ai-icon {
        color: var(--ui-warning);
      }

      &.is-success > .ai-icon {
        color: var(--ui-success);
      }
    }

    .ai-input {
      box-sizing: border-box;
      width: 100%;
      min-height: 34px;
      padding: 6px 10px;
      border: 1px solid var(--ui-border-strong);
      border-radius: var(--ui-radius-sm);
      background: var(--ui-surface);
      color: var(--ui-text);
      font: inherit;
      font-size: 13px;

      &:focus {
        border-color: var(--ui-primary);
        outline: none;
        box-shadow: var(--ui-focus);
      }
    }

    textarea.ai-input {
      resize: vertical;
    }

    .ai-field {
      display: flex;
      flex-direction: column;
      gap: 5px;
      margin: 0;
    }

    .ai-field__label {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 8px;
      color: var(--ui-text);
      font-size: 13px;
      font-weight: 500;
    }

    .ai-field__hint {
      color: var(--ui-text-muted);
      font-size: 12px;
      font-weight: 400;
    }

    .ai-check {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 3px 0;
      color: var(--ui-text);
      font-size: 13px;
      cursor: pointer;

      &.is-group {
        margin-top: 4px;
        font-weight: 500;
      }

      input {
        margin: 0;
        accent-color: var(--ui-primary);
      }
    }

    .ai-spinner {
      display: inline-block;
      flex: 0 0 auto;
      width: 14px;
      height: 14px;
      border: 2px solid var(--ui-primary-soft);
      border-top-color: var(--ui-primary);
      border-radius: 50%;
      animation: ai-spin 0.8s linear infinite;

      &--small {
        width: 11px;
        height: 11px;
        border-width: 2px;
      }
    }

    .ai-evidence-number {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 5px;
      border-radius: 999px;
      background: var(--ui-primary-soft);
      color: var(--ui-primary);
      font-size: 12px;
      font-weight: 600;
    }

    .ai-markdown {
      color: var(--ui-text);
      line-height: 1.55;
      overflow-wrap: anywhere;

      > :first-child {
        margin-top: 0;
      }

      > :last-child {
        margin-bottom: 0;
      }

      h1,
      h2,
      h3,
      h4 {
        margin: 14px 0 6px;
        font-size: 15px;
        font-weight: 600;
      }

      p,
      ul,
      ol {
        margin: 6px 0;
      }

      // A global reset removes list markers: bring them back for answers.
      ul,
      ol {
        padding-left: 22px;
      }

      ul {
        list-style: disc;
      }

      ol {
        list-style: decimal;
      }

      ul ul,
      ol ul {
        list-style: circle;
      }

      li {
        display: list-item;
      }

      li + li {
        margin-top: 2px;
      }

      a {
        color: var(--ui-primary);
      }

      code {
        padding: 1px 5px;
        border-radius: 4px;
        background: var(--ui-bg-strong);
        font-size: 12px;
      }

      pre {
        padding: 10px;
        border-radius: var(--ui-radius-sm);
        background: var(--ui-bg-strong);
        overflow-x: auto;

        code {
          padding: 0;
          background: none;
        }
      }

      blockquote {
        margin: 6px 0;
        padding: 2px 12px;
        border-left: 3px solid var(--ui-border-strong);
        color: var(--ui-text-muted);
      }

      table {
        display: block;
        max-width: 100%;
        margin: 8px 0;
        border-collapse: collapse;
        font-size: 13px;
        overflow-x: auto;
        // The theme's thin pale scrollbars are hard to notice: this one shows hidden columns.
        scrollbar-color: var(--ui-text-muted) var(--ui-bg-strong);
        scrollbar-width: auto;
      }

      th,
      td {
        padding: 5px 10px;
        border: 1px solid var(--ui-border);
        text-align: left;
        vertical-align: top;
      }

      th {
        background: var(--ui-surface-soft);
        font-weight: 500;
      }

      &.is-streaming > :last-child::after {
        content: '';
        display: inline-block;
        width: 7px;
        height: 14px;
        margin-left: 2px;
        background: var(--ui-primary);
        vertical-align: text-bottom;
        animation: ai-blink 1s steps(2) infinite;
      }
    }

    .ai-cite {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      margin: 0 1px;
      padding: 0 4px;
      border-radius: 999px !important;
      background: var(--ui-primary-soft);
      color: var(--ui-primary);
      font: inherit;
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      vertical-align: 1px;
      cursor: pointer;

      &:hover {
        background: var(--ui-primary);
        color: #fff;
      }
    }
  }

  @keyframes ai-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes ai-blink {
    to {
      visibility: hidden;
    }
  }
</style>
